import type { Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';

export type DealHealthState = 'critical' | 'warning' | 'healthy' | 'na';

export const healthScoreSegmentWeights = [2, 5, 3];

export const healthScoreGridTemplateColumns = healthScoreSegmentWeights.map(weight => `${weight}fr`).join(' ');

export const getHealthScoreStateFromScore = (score: number | null): DealHealthState => {
  if (score === null) {
    return 'na';
  }

  if (score <= 19) {
    return 'critical';
  }

  if (score <= 69) {
    return 'warning';
  }

  return 'healthy';
};

export const getHealthScoreValue = (score: number | null | undefined) => {
  const normalizedScore = score == null ? null : Math.min(100, Math.max(0, Math.round(score)));

  return {
    aiAnalysis: null,
    error: false,
    healthState: getHealthScoreStateFromScore(normalizedScore),
    loading: false,
    score: normalizedScore,
  };
};

export const getHealthScoreColor = (healthState: DealHealthState): ((theme: Theme) => string) => {
  if (healthState === 'na') {
    return theme => theme.palette.action.hover;
  }

  if (healthState === 'critical') {
    return theme => theme.palette.error.main;
  }

  if (healthState === 'warning') {
    return theme => theme.palette.warning.main;
  }

  return theme => theme.palette.success.main;
};

export const getHealthScorePopoverTitle = (healthState: DealHealthState, t: TFunction): string | undefined => {
  if (healthState === 'critical') {
    return t('Critical - Immediate attention needed');
  }

  if (healthState === 'warning') {
    return t('Warning - Monitor closely');
  }

  if (healthState === 'healthy') {
    return t('Healthy - No changes needed');
  }

  return undefined;
};

export const getHealthScoreGridColumn = (score: number): number => {
  if (score <= 19) {
    return 1;
  }

  if (score <= 69) {
    return 2;
  }

  return 3;
};

export const healthScoreSegmentMinimumFillPercent = 10;

export const getHealthScoreSegmentFillWidth = (score: number, segmentIndex: number): string => {
  const totalWeight = healthScoreSegmentWeights.reduce((sum, weight) => sum + weight, 0);
  const progressWeight = (Math.min(Math.max(score, 0), 100) / 100) * totalWeight;
  const previousWeight = healthScoreSegmentWeights.slice(0, segmentIndex).reduce((sum, weight) => sum + weight, 0);
  const segmentWeight = healthScoreSegmentWeights[segmentIndex];
  const segmentProgress = Math.min(Math.max(progressWeight - previousWeight, 0), segmentWeight);
  let fillPercent = (segmentProgress / segmentWeight) * 100;

  const activeSegmentIndex = getHealthScoreGridColumn(score) - 1;

  if (segmentIndex === activeSegmentIndex && fillPercent === 0) {
    fillPercent = healthScoreSegmentMinimumFillPercent;
  }

  return `${fillPercent}%`;
};
